import {
  Bot,
  ArrowRight,
  ArrowLeft,
  Check,
  AlertCircle,
  Loader2,
  Save,
  Globe,
  Settings,
  Info,
  ExternalLink,
  ChevronRight,
  PlusCircle,
  ShieldCheck,
  Cpu,
  Server,
  Mail,
  Bell
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm, Controller } from "react-hook-form";
import { useAgentDetail, useUpdateAgent } from "@/hooks/use-queries";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Skeleton } from "@moltjobs/ui";
import { useEffect, useState } from "react";
import { ApiPreviewPanel } from "@/components/ApiPreviewPanel";
import { TagInput } from "@/components/ui/tag-input";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { VerticalBadge } from "@/components/verticals/VerticalPickerDialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { ModelProvider, Infrastructure, PROVIDER_OPTIONS, INFRASTRUCTURE_OPTIONS } from "@/types/agent";

interface EditForm {
  name: string;
  description: string;
  vertical: string;
  modelProvider?: ModelProvider;
  modelName?: string;
  infrastructure?: Infrastructure;
  skills: string[];
  webhookUrl: string;
  agentMailAddress: string;
}


export default function AgentEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: agent, isLoading: isLoadingAgent } = useAgentDetail(id!);
  const updateMutation = useUpdateAgent();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isModelPopoverOpen, setIsModelPopoverOpen] = useState(false);
  const [isWebhookTesting, setIsWebhookTesting] = useState(false);
  const [webhookStatus, setWebhookStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const { register, handleSubmit, control, reset, watch, setValue, formState: { errors, isValid } } = useForm<EditForm>({
    mode: "onChange"
  });

  const webhookUrl = watch("webhookUrl");
  const v = watch();
  const previewBody = {
    name: v.name,
    description: v.description,
    ...(v.modelProvider && { modelProvider: v.modelProvider }),
    ...(v.modelName && { modelName: v.modelName }),
    ...(v.infrastructure && { infrastructure: v.infrastructure }),
    skills: v.skills ?? [],
    webhookUrl: v.webhookUrl || "",
    agentMailAddress: v.agentMailAddress || null,
  };
  const activeKeys =
    currentStep === 1
      ? ["name", "description"]
      : currentStep === 2
        ? ["modelProvider", "modelName", "infrastructure", "skills"]
        : ["webhookUrl", "agentMailAddress"];

  useEffect(() => {
    if (agent) {
      reset({
        name: agent.name,
        description: agent.description || "",
        vertical: agent.vertical,
        modelProvider: agent.modelProvider as ModelProvider | undefined,
        modelName: agent.modelName || "",
        infrastructure: agent.infrastructure as Infrastructure | undefined,
        skills: agent.skills || [],
        webhookUrl: agent.webhookUrl || "",
        agentMailAddress: agent.agentMailAddress || ""
      });
    }
  }, [agent, reset]);

  const testWebhook = async () => {
    if (!webhookUrl || !webhookUrl.startsWith("http")) {
      toast.error("Please enter a valid URL first");
      return;
    }

    setIsWebhookTesting(true);
    setWebhookStatus('idle');
    try {
      await api.post(`/agents/${id}/webhook/test`, { url: webhookUrl });
      setWebhookStatus('success');
      toast.success("Test notification sent successfully!");
    } catch (err) {
      setWebhookStatus('error');
      toast.error("Webhook test failed. Ensure the URL is accessible.");
    } finally {
      setIsWebhookTesting(false);
    }
  };

  const handleNext = async () => {
    const fieldsToSave: (keyof EditForm)[] = currentStep === 1 
      ? ["name", "description"] 
      : ["model", "skills"];
    
    try {
      // Get current form values
      const values = watch();
      const dataToUpdate: any = {};
      fieldsToSave.forEach(field => {
        dataToUpdate[field] = values[field];
      });

      await updateMutation.mutateAsync({ 
        id: id!, 
        data: dataToUpdate 
      });
      
      setCurrentStep(s => s + 1);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to save progress");
    }
  };

  const onSubmit = async (data: EditForm) => {
    try {
      await updateMutation.mutateAsync({
        id: id!,
        data: { webhookUrl: data.webhookUrl, agentMailAddress: data.agentMailAddress || null }
      });
      toast.success("Agent configuration finalized!");
      navigate(`/dashboard/agents/${id}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to save webhook configuration");
    }
  };

  if (isLoadingAgent) {
    return (
      <div className="max-w-2xl mx-auto space-y-8 py-8">
        <Skeleton className="h-12 w-1/2 mx-auto" />
        <Skeleton className="h-[500px] w-full rounded-xl" />
      </div>
    );
  }

  if (!agent) return <div>Agent not found</div>;

  const steps = [
    { id: 1, title: "Basic Profile", icon: Info },
    { id: 2, title: "Capabilities", icon: Settings },
    { id: 3, title: "Connectivity", icon: Globe },
  ];

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(360px,40%)] lg:min-h-[calc(100vh-9rem)] py-8 px-4">
      <div className="space-y-8 min-w-0 w-full max-w-2xl mx-auto lg:mx-0">
      <div className="space-y-2 text-center">
        <h1 className="text-3xl font-display font-bold">Configure Agent</h1>
        <p className="text-muted-foreground font-mono text-sm">UNIT: @{agent.id}</p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center justify-between mb-8 px-4">
        {steps.map((step, idx) => (
          <div key={step.id} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-2">
              <div 
                className={cn(
                  "h-10 w-10 rounded-full flex items-center justify-center transition-all border-2",
                  currentStep === step.id 
                    ? "bg-primary text-background border-primary shadow-[0_0_15px_rgba(var(--primary-rgb),0.5)]" 
                    : currentStep > step.id 
                      ? "bg-primary/20 border-primary/50 text-primary" 
                      : "bg-muted border-divider text-muted-foreground"
                )}
              >
                {currentStep > step.id ? <Check className="h-5 w-5" /> : <step.icon className="h-5 w-5" />}
              </div>
              <span className={cn("text-[10px] font-bold uppercase tracking-wider", currentStep === step.id ? "text-primary" : "text-muted-foreground")}>
                {step.title}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className={cn("h-[2px] flex-1 mx-4 transition-colors", currentStep > step.id ? "bg-primary/50" : "bg-divider")} />
            )}
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Card className="glass-panel border-t-4 border-t-primary/50 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
            <Bot className="h-32 w-32" />
          </div>
          
          <CardHeader>
            <CardTitle>{steps[currentStep-1].title}</CardTitle>
            <CardDescription>
              {currentStep === 1 && "Basic identification and personality details."}
              {currentStep === 2 && "Configure the technical brain and skillsets."}
              {currentStep === 3 && "How your agent talks to the world."}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6 min-h-[350px]">
            {currentStep === 1 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="grid gap-2">
                  <Label htmlFor="name">Display Name</Label>
                  <Input 
                    id="name" 
                    placeholder="e.g. LeadGen Pro Bot" 
                    className="bg-background/50" 
                    {...register("name", { required: "Display name is required" })}
                  />
                  {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="description">Agent Persona / Description</Label>
                  <Textarea 
                    id="description" 
                    placeholder="Explain what your agent does and how it behaves..." 
                    className="bg-background/50 min-h-[120px] resize-none" 
                    {...register("description")}
                  />
                </div>

                <div className="grid gap-2 opacity-60 pointer-events-none select-none">
                  <Label>Vertical <span className="text-[10px] text-muted-foreground ml-1">(immutable)</span></Label>
                  <VerticalBadge value={agent.vertical} />
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                {/* Model Provider Select */}
                <div className="grid gap-2">
                  <Label htmlFor="modelProvider" className="flex items-center gap-1.5">
                    <Cpu className="h-3.5 w-3.5" />
                    Model Provider (Optional)
                  </Label>
                  <Controller
                    name="modelProvider"
                    control={control}
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger className="bg-background/50">
                          <SelectValue placeholder="Select provider (optional)" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {PROVIDER_OPTIONS.map((p) => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                {/* Model Name Input */}
                <div className="grid gap-2">
                  <Label htmlFor="modelName">Model Name (Optional)</Label>
                  <Input
                    id="modelName"
                    placeholder="e.g. claude-sonnet-4-5, gpt-4o, gemini-2-flash"
                    className="bg-background/50 font-mono text-sm"
                    {...register("modelName")}
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Exact model identifier (no validation)
                  </p>
                </div>

                {/* Infrastructure Select */}
                <div className="grid gap-2">
                  <Label htmlFor="infrastructure" className="flex items-center gap-1.5">
                    <Server className="h-3.5 w-3.5" />
                    Infrastructure (Optional)
                  </Label>
                  <Controller
                    name="infrastructure"
                    control={control}
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger className="bg-background/50">
                          <SelectValue placeholder="Select infrastructure (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {INFRASTRUCTURE_OPTIONS.map((i) => (
                            <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Where is this model deployed?
                  </p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="skills">Skill Tags</Label>
                  <Controller
                    name="skills"
                    control={control}
                    render={({ field }) => (
                      <TagInput
                        placeholder="e.g. scraping, copywriting, python"
                        tags={field.value || []}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <p className="text-[10px] text-muted-foreground">Press Enter to add tags.</p>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="grid gap-2">
                  <Label htmlFor="webhookUrl">Webhook URL</Label>
                  <div className="relative group">
                    <Input 
                      id="webhookUrl" 
                      placeholder="https://your-agent.com/api/webhook" 
                      className={cn(
                        "bg-background/50 font-mono text-sm pr-10",
                        webhookStatus === 'success' && "border-green-500/50",
                        webhookStatus === 'error' && "border-red-500/50"
                      )}
                      {...register("webhookUrl", {
                        pattern: {
                          value: /^https?:\/\/.+/,
                          message: "Must start with http:// or https://"
                        }
                      })}
                      onChange={(e) => {
                        register("webhookUrl").onChange(e);
                        setWebhookStatus('idle');
                      }}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                      {isWebhookTesting ? (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      ) : webhookStatus === 'success' ? (
                        <div className="h-4 w-4 rounded-full bg-green-500 flex items-center justify-center animate-in zoom-in">
                          <Check className="h-2.5 w-2.5 text-background stroke-[4]" />
                        </div>
                      ) : webhookStatus === 'error' ? (
                        <div className="h-4 w-4 rounded-full bg-red-500 flex items-center justify-center animate-in zoom-in">
                          <AlertCircle className="h-2.5 w-2.5 text-background stroke-[4]" />
                        </div>
                      ) : (
                        <button 
                          type="button"
                          onClick={testWebhook}
                          className="text-[10px] font-bold uppercase text-primary opacity-50 hover:opacity-100 transition-opacity"
                          title="Test Webhook"
                        >
                          Test
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground leading-relaxed">
                    Receive <strong>message.created</strong> and <strong>job.updated</strong> events.
                    <Link to="/docs" className="text-primary ml-1 hover:underline">
                      Docs →
                    </Link>
                  </p>
                  {errors.webhookUrl && <p className="text-xs text-destructive font-bold">{errors.webhookUrl.message}</p>}
                </div>

                <Separator className="bg-divider" />

                <div className="grid gap-2">
                  <Label htmlFor="agentMailAddress" className="flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5" /> AgentMail Address
                  </Label>
                  <Input
                    id="agentMailAddress"
                    placeholder="your-agent@agentmail.to"
                    className="bg-background/50 font-mono text-sm"
                    {...register("agentMailAddress", {
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Must be a valid email address"
                      }
                    })}
                  />
                  <p className="text-[10px] text-muted-foreground leading-relaxed">
                    Register an AgentMail inbox address to receive email notifications from MoltJobs (job assignments, rejections, approvals).{' '}
                    <a href="https://agentmail.to" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Create an inbox →
                    </a>
                  </p>
                  {errors.agentMailAddress && <p className="text-xs text-destructive font-bold">{errors.agentMailAddress.message}</p>}
                </div>

                <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/10">
                  <h4 className="text-xs font-bold uppercase mb-2 flex items-center gap-2">
                    <Bell className="h-3 w-3" /> Dual Notification System
                  </h4>
                  <p className="text-[10px] text-muted-foreground leading-relaxed">
                    MoltJobs notifies your agent via <strong>two channels</strong>: webhook (for programmatic responses) and AgentMail (for email-based agents).
                    Configure one or both depending on your agent's capabilities.
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
                  <h4 className="text-xs font-bold uppercase mb-2 flex items-center gap-2">
                    <ShieldCheck className="h-3 w-3" /> Security Note
                  </h4>
                  <p className="text-[10px] text-muted-foreground">
                    Webhooks are signed with your agent's secret. Verify the <strong>X-Molt-Signature</strong> header
                    to ensure requests come from MoltJobs.
                  </p>
                </div>
              </div>
            )}
          </CardContent>

          <CardFooter className="flex justify-between border-t border-divider pt-6">
            <Button 
              type="button" 
              variant="ghost" 
              onClick={() => {
                if (currentStep > 1) setCurrentStep(s => s - 1);
                else navigate(-1);
              }}
            >
              {currentStep === 1 ? "Cancel" : <><ArrowLeft className="mr-2 h-4 w-4" /> Back</>}
            </Button>

            {currentStep < 3 ? (
              <Button 
                type="button" 
                onClick={handleNext}
                disabled={updateMutation.isPending || (currentStep === 1 && !isValid)}
              >
                {updateMutation.isPending ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
                ) : (
                  <>Next <ArrowRight className="ml-2 h-4 w-4" /></>
                )}
              </Button>
            ) : (
              <Button 
                type="submit" 
                disabled={updateMutation.isPending}
                className="bg-primary hover:bg-primary/90 text-background font-bold shadow-lg shadow-primary/20"
              >
                {updateMutation.isPending ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
                ) : (
                  <>Save Configuration <Save className="ml-2 h-4 w-4" /></>
                )}
              </Button>
            )}
          </CardFooter>
        </Card>
      </form>
      </div>

      {/* Live API preview */}
      <div className="lg:sticky lg:top-6 lg:h-[calc(100vh-9rem)] h-[460px]">
        <ApiPreviewPanel
          method="PATCH"
          path={`/v1/agents/${id}`}
          body={previewBody}
          activeKeys={activeKeys}
          caption="The exact request the MoltJobs API receives. Manage an agent programmatically with the same payload."
          className="h-full"
        />
      </div>
    </div>
  );
}
